<script lang="ts">
    import { onDestroy, onMount } from "svelte"
    import { getKirbyPage, kirbyFetch } from "../../../../api/kirby/api"
    import SectionTitleWrapper from "../../../widgets/SectionTitleWrapper.svelte"
    import VerticalArticlesPreview from "./VerticalArticlesPreview.svelte"
    import { eventlocations, eventseries, lang, location, topics } from "../../../../store"
    import HorizontalSlider from "./HorizontalSlider.svelte"
    import MiniLoader from "../../../widgets/MiniLoader.svelte"
    import { _ } from "svelte-i18n"
    import { unlistedToken } from "../../../../../../api/hooks/config-client"
    import { articleQuery } from "./helper"
    import { onNavigate, removeOnNavigate } from "../../../../functionTools/routing"
    import { baseURL } from "../../../../configs/config"
    import { fallbackLocale } from "../../../../i18n"

    let {
        articlesPreview,
        fullTextSearch = "",
        fullWidth = false,
        pageTemplate = "",
    }: {
        articlesPreview: ArticlePreview
        fullTextSearch?: string
        fullWidth?: boolean
        pageTemplate?: string
    } = $props()
    function toBoolean(value: unknown): boolean {
        if (typeof value === "boolean") return value
        if (typeof value === "number") return value === 1
        if (typeof value === "string") {
            const normalized = value.trim().toLowerCase()
            return normalized === "true" || normalized === "1" || normalized === "yes"
        }
        return false
    }
    function toTimestamp(value?: string | null): number {
        if (!value) return -Infinity
        const timestamp = new Date(value).getTime()
        return Number.isFinite(timestamp) ? timestamp : -Infinity
    }
    function isEventsOverviewPath(path?: string): boolean {
        if (!path) return false
        const normalizedPath = path.replace(/\/+$/, "")
        const segments = normalizedPath.split("/").filter(Boolean)
        const lastSegment = segments[segments.length - 1]?.toLowerCase()
        return lastSegment === "veranstaltungen" || lastSegment === "events"
    }
    function isHomePath(path?: string): boolean {
        if (!path) return false
        const normalizedPath = path.replace(/\/+$/, "")
        const segments = normalizedPath.split("/").filter(Boolean)
        // "/", "/de", "/en" usw. als Startseite behandeln
        if (segments.length === 0) return true
        const firstSegment = segments[0]?.toLowerCase()
        return firstSegment === "de" || firstSegment === "en"
    }
    function isVlogsOverviewPath(path?: string): boolean {
        if (!path) return false
        const normalizedPath = path.replace(/\/+$/, "")
        const segments = normalizedPath.split("/").filter(Boolean)
        const lastSegment = segments[segments.length - 1]?.toLowerCase()
        return lastSegment === "vlogs" || lastSegment === "vlog"
    }
    const normalizedTemplate = $derived((pageTemplate || "").toLowerCase())
    const isEventsTemplate = $derived(normalizedTemplate === "events")
    const isEventsOverview = $derived(isEventsTemplate || isEventsOverviewPath($location.path))
    const isVlogsTemplate = $derived(normalizedTemplate === "vlogs")
    const isVlogsOverview = $derived(isVlogsTemplate || isVlogsOverviewPath($location.path))
    const isHomeTemplate = $derived(normalizedTemplate === "home")
    const isHomeOverview = $derived(isHomeTemplate || isHomePath($location.path))
    const isNewsTemplate = $derived(normalizedTemplate === "news")
    let articles: ArticleEntryContent[] = $state([])
    let main_article: ArticleEntryContent = $state()
    let fallbackPrimaryArticle: ArticleEntryContent | null = null
    let fallbackMainArticles: ArticleEntryContent[] = []
    let page_reference = $state("")
    let loading = $state(true)

    const homepageModeEnabled = $derived(toBoolean(articlesPreview.homepage_mode))
    const manualSelectionEnabled = $derived(toBoolean(articlesPreview.manual_selection))
    let offset = $state(0)
    let hasMoreEntries = $state(true)
    const limit = $derived(homepageModeEnabled ? 6 : 10)
    let selectedTopicFilters = $state([])
    let selectedTypeFilters = $state([])
    let selectedEventseriesFilers = $state([])
    let selectedEventlocationFilters = $state([])
    let selectedPressTypeFilters = $state([])
    let selectedVlogFilters = $state([])
    let filtersInitialized = false
    let nestedLoading = $state(false)

    let missingEntries = $state(0)
    let loadSeq = 0

    function resetFetchedState() {
        articles = []
        offset = 0
        hasMoreEntries = true
        missingEntries = 0
    }

    async function loadArticles(
        reset: boolean = false,
        selectedTopicFilters: string[] = [],
        selectedTypeFilters: string[] = [],
        selectedEventseriesFilers: string[] = [],
        selectedEventlocationFilters: string[] = [],
        selectedPressTypeFilters: string[] = [],
        selectedVlogFilters: string[] = [],
        requestSeq: number | null = null
    ) {
        if (reset) {
            resetFetchedState()
            fallbackPrimaryArticle = null
            fallbackMainArticles = []
            loadSeq += 1
        }
        const currentSeq = requestSeq ?? loadSeq
        loading = true
        if (!homepageModeEnabled && missingEntries > limit) missingEntries = limit
        const articleTypeTokens = (articlesPreview.article_types || "")
            .split(/,\s*/)
            .map((type) => type.trim().toLowerCase())
            .filter((type) => type)
        const eventTypeOnly =
            selectedTypeFilters.length === 0 &&
            articleTypeTokens.length > 0 &&
            articleTypeTokens.every((type) => type === "event")
        const filterEventsOnly = eventTypeOnly || isEventsOverview

        // Vlogs sind News-Artikel, die nur durch is_vlog Toggle markiert werden
        // Kein separater article_type, daher nur Pfad-basierte Filterung
        const filterVlogsOnly = isVlogsOverview

        let articleTemplates = articleTypeTokens.map((type) => {
            if (type === "event") return "newsarticle"
            return `${type}article`
        })
        if (articleTemplates.length === 0) articleTemplates = ["newsarticle"]

        if (selectedTypeFilters.length > 0) {
            articleTemplates = selectedTypeFilters.map((type) => {
                const lowerType = type.toLocaleLowerCase()
                // Vlogs sind News-Artikel, daher zu newsarticle mappen
                if (lowerType === "vlogarticle") return "newsarticle"
                return lowerType
            })
        }
        let query = `site.index.filterBy('intendedTemplate', 'in', ['${articleTemplates.join("','")}'])`

        if (filterEventsOnly) {
            // Events: nur Einträge, bei denen das Toggle wirklich auf "true" steht
            // (Kirby speichert den Toggle als String, z.B. "true")
            query += `.filterBy('is_event', '==', 'true')`
        }

        // Vlog-Filter: Prüfe ob vlogFilter aktiv ist
        const hasVlogFilter = selectedVlogFilters.length > 0 || filterVlogsOnly
        if (hasVlogFilter) {
            // Vlogs: alle Einträge, bei denen das Toggle gesetzt ist
            query += `.filterBy('is_vlog', '!=', '')`
        }

        if (selectedEventseriesFilers.length > 0) {
            const availableEventseries = Object.values($eventseries)
            const eventseriesIds = availableEventseries
                .filter((t) => selectedEventseriesFilers.includes(t.slug))
                .map((t) => `- '${t.uuid}'`)
            if (eventseriesIds.length > 0) {
                query += `.filterBy('eventseries_reference', 'in', ["${eventseriesIds.join('","')}"])`
            }
        }
        if (selectedEventlocationFilters.length > 0) {
            const availableEventlocations = Object.values($eventlocations)
            const eventLocationIds = availableEventlocations
                .filter((t) => selectedEventlocationFilters.includes(t.slug))
                .map((t) => `- '${t.uuid}'`)
            if (eventLocationIds.length > 0) {
                query += `.filterBy('eventlocation_reference', 'in', ["${eventLocationIds.join('","')}"])`
            }
        }
        if (selectedPressTypeFilters.length > 0) {
            query += `.filterBy('presstype', 'in', ["${selectedPressTypeFilters.join('","')}"])`
        }

        // Für nicht-deutsche Sprachen nur Artikel laden, die eine EN-Version haben.
        // Kirby-Toggle speichert "true"/"false" als String – wir schließen explizit "true" aus.
        if ($lang !== fallbackLocale) {
            query += `.filterBy('no_en_version', '!=', 'true')`
        }

        if (fullTextSearch) query += `.search('${fullTextSearch}')`
        query += `.sortBy('publication_date', 'desc')`

        if ($location.search.includes("preview=true") && $location.search.includes("pw=" + unlistedToken)) void 0
        else query += `.filterBy('status', 'listed')`

        try {
            let res: KirbyResponse
            if (manualSelectionEnabled)
                query += `.filterBy('uuid', 'in', ${JSON.stringify(articlesPreview.article_pages)})`
            const usedLimit = missingEntries > 0 ? missingEntries : limit

            res = await kirbyFetch<KirbyResponse>({
                query,
                limit: usedLimit,
                select: articleQuery,
                offset,
            })
            if (currentSeq !== loadSeq) return
            if (!res) {
                hasMoreEntries = false
                missingEntries = 0
                return
            }

            // API kann { data, pagination } oder (selten) nur Array liefern
            const responseData = Array.isArray(res)
                ? res
                : Array.isArray((res as { data?: unknown })?.data)
                  ? (res as KirbyResponse).data
                  : []

            // Client-seitige Filterung als Fallback für Events und Vlogs
            // (falls die KQL-Filterung nicht greift)
            let filteredData = responseData
            if (filterEventsOnly) {
                // Nur Events anzeigen
                filteredData = filteredData.filter((article: any) => {
                    const isEvent = toBoolean(article.is_event)
                    return isEvent === true
                })
            }
            if (hasVlogFilter) {
                // Nur Vlogs anzeigen
                filteredData = filteredData.filter((article: any) => {
                    const isVlog = toBoolean(article.is_vlog)
                    return isVlog === true
                })
            }

            // Veranstaltungen selektiv auf Startseite / Presseseite anzeigen
            // Events-Übersicht (Veranstaltungsseite) bleibt davon unberührt
            filteredData = filteredData.filter((article: any) => {
                const isEvent = toBoolean(article.is_event)
                if (!isEvent) return article

                // Auf der Veranstaltungsübersicht sollen alle Events sichtbar bleiben
                if (isEventsOverview) return true

                const showOnHome = toBoolean((article as any)?.show_on_home)
                const showInPress = toBoolean((article as any)?.show_in_press)

                // Home: Veranstaltungs-News nur anzeigen, wenn explizit erlaubt
                if (isHomeOverview) {
                    return showOnHome
                }

                // Neuigkeiten / Presse-Seite: Veranstaltungs-News nur anzeigen, wenn explizit erlaubt
                if (isNewsTemplate) {
                    return showInPress
                }

                // Auf anderen Seiten verhalten sich Events wie bisher
                return true
            })

            const visibleArticles =
                $lang === fallbackLocale
                    ? filteredData
                    : filteredData.filter((article) => !toBoolean((article as any)?.no_en_version))

            const sortedArticles = visibleArticles.sort(
                (a, b) => new Date(b.publication_date).getTime() - new Date(a.publication_date).getTime()
            )
            let articlesLengthBeforeRequest = reset ? 0 : articles.length
            if (reset) articles = sortedArticles
            else {
                // keep order by publication date while merging new results
                const merged = [...articles, ...sortedArticles]
                const uniqueById = merged.filter(
                    (article, index, self) => index === self.findIndex((a) => a.id === article.id)
                )
                articles = uniqueById.sort((a, b) => toTimestamp(b.publication_date) - toTimestamp(a.publication_date))
            }
            if (!fallbackPrimaryArticle && articlesLengthBeforeRequest === 0 && sortedArticles.length > 0) {
                fallbackPrimaryArticle = sortedArticles[0]
            }
            fallbackMainArticles = [...articles]

            const pagination = res.pagination ?? { page: 1, pages: 1, limit: usedLimit }
            const paginationLimit = pagination.limit ?? usedLimit
            offset += paginationLimit

            // hasMoreEntries ausschließlich anhand der Backend-Pagination bestimmen.
            // Durch unsere zusätzlichen Filter (Events, Sprache, etc.) kann die Anzahl
            // der angezeigten Artikel kleiner als `usedLimit` sein – das bedeutet aber
            // nicht, dass es im Backend keine weiteren Seiten mehr gibt.
            hasMoreEntries = pagination.page < pagination.pages

            if (selectedTopicFilters.length > 0) {
                const topicIds = Object.values($topics)
                    .filter((t) => selectedTopicFilters.includes(t.slug))
                    .map((t) => `- '${t.uuid}'`)
                articles = articles.filter((article) => {
                    if (!article?.topics_reference) return false
                    return topicIds.some((topicId) => article.topics_reference.includes(topicId))
                })
            }
            if (homepageModeEnabled) {
                if (!articlesPreview.main_article_page || !articlesPreview.main_article_page[0]) {
                    main_article = fallbackPrimaryArticle || fallbackMainArticles[0]
                } else {
                    main_article = await getKirbyPage<"articleEntry">(
                        articlesPreview.main_article_page[0],
                        articleQuery
                    )
                }
                if ($lang !== fallbackLocale && toBoolean((main_article as any)?.no_en_version)) {
                    main_article = fallbackPrimaryArticle || fallbackMainArticles[0]
                }
                if (main_article) {
                    articles = articles.filter((article) => article?.id !== main_article?.id)
                }
                const referenceId = articlesPreview.page_reference?.[0]
                if (referenceId) {
                    const page = await getKirbyPage(referenceId)
                    const currentLang = String($lang || fallbackLocale)
                    const localizedPath = page?.url?.split(`/${currentLang}/`).pop()

                    if (localizedPath) page_reference = `/${currentLang}/${localizedPath}`
                    else if (page?.id) page_reference = `/${currentLang}/${page.id}`
                    else if (page?.slug) page_reference = `/${currentLang}/${page.slug}`
                }
            }

            const previousArticles = articles
            const deduped = articles.filter(
                (article, index, self) => index === self.findIndex((a) => a.id === article.id)
            )
            const sorted = [...deduped].sort(
                (a, b) => toTimestamp(b.publication_date) - toTimestamp(a.publication_date)
            )
            if (
                sorted.length !== previousArticles.length ||
                sorted.some((article, index) => article.id !== previousArticles[index]?.id)
            ) {
                articles = sorted
            }
            const amountOfArticlesFetched = articles.length - articlesLengthBeforeRequest

            // hasMoreEntries nur anhand Backend-Pagination (siehe oben). Nicht nach
            // amountOfArticlesFetched abbrechen: Durch clientseitige Filter (Sprache,
            // showInPress, etc.) können 0 neue Artikel ankommen, obwohl weitere Seiten
            // existieren. Nur wenn das Backend eine leere Seite liefert, ist Schluss.
            if (responseData.length === 0 && !reset) {
                hasMoreEntries = false
            }

            if (nestedLoading) missingEntries = Math.max(missingEntries - amountOfArticlesFetched, 0)
            else missingEntries = Math.max(usedLimit - amountOfArticlesFetched, 0)

            if (missingEntries > 0 && hasMoreEntries && sortedArticles.length > 0) {
                nestedLoading = true
                await loadMore(currentSeq)
                nestedLoading = false
                return
            }

            if (!hasMoreEntries) missingEntries = 0

            if (currentSeq !== loadSeq) return
        } catch (error) {
            console.error("Failed to load articles", error)
            if (currentSeq === loadSeq) {
                hasMoreEntries = false
                missingEntries = 0
            }
        } finally {
            if (currentSeq === loadSeq) {
                nestedLoading = false
                loading = false
            }
        }
    }

    async function loadMore(seqOrEvent: number | MouseEvent | null = null) {
        const seq = typeof seqOrEvent === "number" ? seqOrEvent : loadSeq
        await loadArticles(
            false,
            selectedTopicFilters,
            selectedTypeFilters,
            selectedEventseriesFilers,
            selectedEventlocationFilters,
            selectedPressTypeFilters,
            selectedVlogFilters,
            seq
        )
    }

    async function onUrlChange(to: string, from: string) {
        if (!to.startsWith(baseURL)) {
            to = baseURL + to
        }
        const url = new URL(to)

        const queryKeys = [
            "filters_topic",
            "filters_type",
            "filters_eventseries",
            "filters_eventlocation",
            "filters_presstype",
            "filters_vlog",
        ]
        let newSelectedTopicFilters: string[] = []
        let newSelectedTypeFilters: string[] = []
        let newSelectedPressTypeFilters: string[] = []
        let newSelectedEventseriesFilers: string[] = []
        let newSelectedEventlocationFilters: string[] = []
        let newSelectedVlogFilters: string[] = []

        queryKeys.forEach((queryKey) => {
            const filtersParam = url.searchParams.get(queryKey)
            if (filtersParam && queryKey === "filters_topic")
                newSelectedTopicFilters.push(...filtersParam.split(",").filter((f) => f))
            else if (filtersParam && queryKey === "filters_type")
                newSelectedTypeFilters.push(...filtersParam.split(",").filter((f) => f))
            else if (filtersParam && queryKey === "filters_eventseries")
                newSelectedEventseriesFilers.push(...filtersParam.split(",").filter((f) => f))
            else if (filtersParam && queryKey === "filters_eventlocation")
                newSelectedEventlocationFilters.push(...filtersParam.split(",").filter((f) => f))
            else if (filtersParam && queryKey === "filters_presstype")
                newSelectedPressTypeFilters.push(...filtersParam.split(",").filter((f) => f))
            else if (filtersParam && queryKey === "filters_vlog")
                newSelectedVlogFilters.push(...filtersParam.split(",").filter((f) => f))
        })

        selectedTopicFilters = newSelectedTopicFilters
        selectedTypeFilters = newSelectedTypeFilters
        selectedEventseriesFilers = newSelectedEventseriesFilers
        selectedEventlocationFilters = newSelectedEventlocationFilters
        selectedPressTypeFilters = newSelectedPressTypeFilters
        selectedVlogFilters = newSelectedVlogFilters
        filtersInitialized = true

        await loadArticles(
            true,
            selectedTopicFilters,
            selectedTypeFilters,
            selectedEventseriesFilers,
            selectedEventlocationFilters,
            selectedPressTypeFilters,
            selectedVlogFilters
        )
    }

    if (typeof window === "undefined" && !filtersInitialized) {
        filtersInitialized = true
        void loadArticles(true, [], [], [], [], [], [])
    }

    onMount(() => {
        onNavigate(onUrlChange)
        onUrlChange($location.url, "")
    })

    onDestroy(() => removeOnNavigate(onUrlChange))
</script>

{#if articlesPreview.direction == "vertical"}
    <section class="full-width" class:homepage={homepageModeEnabled}>
        <section class="articles-container">
            <SectionTitleWrapper
                type="reference"
                title={articlesPreview.sticky_header_title}
                referenceTitle={$_("general.All-news")}
                page={page_reference || `/${String($lang || fallbackLocale)}/neuigkeiten`}
                width="normal"
                padding={false}
                enabled={homepageModeEnabled}
            >
                {#snippet content()}
                    {#if articles.length}
                        <VerticalArticlesPreview
                            {fullWidth}
                            {articles}
                            type={homepageModeEnabled ? "homepage" : "subpage"}
                            primary_article={homepageModeEnabled ? main_article : void 0}
                        />
                    {:else if !loading}
                        <p>{$_("general.no-entries-found")}</p>
                    {/if}

                    {#if loading}
                        <MiniLoader />
                    {:else if hasMoreEntries && !homepageModeEnabled}
                        <div style="display: flex; justify-content: center; width: 100%;">
                            <button onclick={() => loadMore()} class="load-more">{$_("general.load-more")}</button>
                        </div>
                    {/if}
                {/snippet}
            </SectionTitleWrapper>
        </section>
    </section>
{:else if articles.length}
    <HorizontalSlider cards={articles} {articlesPreview} {page_reference} />
{/if}

<style lang="less">
    .full-width {
        min-height: 80vh;
        width: 100%;
        display: flex;
        justify-content: center;
        background-color: var(--bg-100);
        &.homepage {
            padding: 0px var(--default-padding);
        }
        .articles-container {
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: 0.6rem;
        }
        .load-more {
            text-transform: uppercase;
            width: fit-content;
            font-size: 1.2rem;
            margin: 1rem 0;
            position: relative;
            display: inline-block; /* keeps pseudo element positioning intact */

            &::after {
                content: "";
                position: absolute;
                left: 0;
                bottom: 0;
                height: 2px; /* line thickness */
                width: 0;
                background-color: currentColor; /* uses text color */
                transition: width 0.3s ease-in-out;
            }

            &:hover {
                cursor: pointer;
                &::after {
                    width: 100%;
                }
            }
        }
    }
</style>
